import { Capacitor } from '@capacitor/core';
import { useQuery } from '@tanstack/react-query';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import {
  createOfflineBootstrapStore,
  type OfflineBootstrapSnapshot,
} from '../../offline/session/offline-bootstrap-store';
import { createPlatformOfflineStorage } from '../../offline/storage/offline-storage';
import { resolveUserBootstrapData } from './user-bootstrap-data';
import { getCachedSelfAppDataOptions } from './user-query-options';
import { UserContext } from './user.context';

// Must be the platform storage (secure storage on native), matching the backend the
// Trackd bootstrap writes to; the browser adapter would read a different physical
// store on a device and never restore the offline session. See spec F2 / T076.
const offlineBootstrapStore = createOfflineBootstrapStore(createPlatformOfflineStorage());

export const UserProvider = ({ children }: PropsWithChildren) => {
  const { firebaseUser } = useAuth();
  const isNative = Capacitor.isNativePlatform();
  const { data } = useQuery({ ...getCachedSelfAppDataOptions(), throwOnError: !isNative });
  const [bootstrapSnapshot, setBootstrapSnapshot] = useState<OfflineBootstrapSnapshot>();

  useEffect(() => {
    let active = true;

    if (!isNative || !firebaseUser?.uid) {
      setBootstrapSnapshot(undefined);
      return () => {
        active = false;
      };
    }

    void offlineBootstrapStore.get(firebaseUser.uid).then((snapshot) => {
      if (active) setBootstrapSnapshot(snapshot);
    });

    return () => {
      active = false;
    };
  }, [firebaseUser?.uid, isNative]);

  useEffect(() => {
    if (!isNative || !firebaseUser?.uid || !data?.data) return;

    void offlineBootstrapStore.save(firebaseUser.uid, {
      firebaseUserId: firebaseUser.uid,
      savedAt: new Date().toISOString(),
      user: data.data,
    });
  }, [data?.data, firebaseUser?.uid, isNative]);

  const userData = resolveUserBootstrapData(data?.data, bootstrapSnapshot, isNative);
  const user = useMemo(() => (userData ? { user: userData } : undefined), [userData]);

  if (!user) return null;

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};
